import { useGameStore } from '@/state/gameStore';
import {
  ASPECTS, aspectLevelUpCost, affinityMultiplier, countAffinityPieces,
  defaultAspectsState,
  type AspectDef, type AspectId, type AspectSlot,
} from '@/domain/aspects';
import { getItem } from '@/domain/items';
import './AspectPanel.css';

/**
 * Aspekt-Selection + Invest UI. Renders all 4 aspects as cards with:
 *   - Unlock-state indicator (locked / available / slotted)
 *   - Primary/Secondary slot pickers (Secondary gated behind Tau-Vault)
 *   - Level invest button (L1 free on first slot, L2-L5 cost CD)
 *   - Affinity-tier preview based on currently equipped items
 *
 * Passives are listed but mostly informational in this session — actual
 * combat effects land in P2.B.2 (next session). Until then the player
 * can pick + level aspects but the values shown are "future-ready".
 */

const AFFINITY_LABEL = (matchingPieces: number): string => {
  const mult = affinityMultiplier(matchingPieces);
  if (mult === 1.0) return `${matchingPieces} Teile · keine Affinität`;
  return `${matchingPieces} Teile · ×${mult.toFixed(2)} Affinität`;
};

export function AspectPanel() {
  const player = useGameStore((s) => s.player);
  const setAspect = useGameStore((s) => s.setAspect);
  const investAspect = useGameStore((s) => s.investAspectLevel);
  const setActiveSkill = useGameStore((s) => s.setActiveSkill);

  if (!player) return null;
  // Fall back to the default state if migration / Supabase-load left the
  // field undefined. The panel always renders so the player can SEE the
  // system; the underlying store actions self-heal by writing through the
  // default when invoked (setAspect / investAspectLevel both touch `aspects`).
  const aspectsRaw = player.aspects ?? defaultAspectsState();
  const cd = player.corruptedData ?? 0;
  const tier = player.highestVaultTier ?? 0;
  const secondaryAvailable = tier >= 3;
  const affinityCounts = countAffinityPieces(player.equipment, getItem);

  // Unlock-state is DERIVED from highestVaultTier, not from the cached
  // `unlocked` array. The cache was meant as a denormalization but got
  // stale as soon as the player cleared more tiers after migration. By
  // computing it here we always reflect the player's CURRENT progression.
  const unlockedSet = new Set<AspectId>(aspectsRaw.unlocked);
  for (const id of Object.keys(ASPECTS) as AspectId[]) {
    if (tier >= ASPECTS[id].unlock.vaultTier) unlockedSet.add(id);
  }
  const aspects = {
    ...aspectsRaw,
    unlocked: Array.from(unlockedSet),
  };

  return (
    <section className="aspect-panel">
      <div className="aspect-panel__header">
        <h2 className="aspect-panel__title">Aspekte</h2>
        <div className="aspect-panel__sub">
          Wähle Primary + Secondary. Affinität verstärkt Passives wenn 3+/6+/8+ Items deinen Aspekt matchen.
        </div>
      </div>

      {(() => {
        // Derive the active-skill pool: union of explicitly-unlocked skills
        // (from setAspect history) AND every active skill from any aspect
        // currently in `unlocked`. This covers cases where migration didn't
        // populate `unlockedActiveSkills` — the user always sees a valid pool
        // matching their unlocked aspects.
        const poolSet = new Set(player.unlockedActiveSkills ?? []);
        for (const aspectId of aspects.unlocked) {
          poolSet.add(ASPECTS[aspectId].activeSkillId);
        }
        const pool = Array.from(poolSet);
        return (
          <div className="aspect-panel__active-skill">
            <span className="aspect-panel__active-label">Aktiver Skill:</span>
            <select
              value={player.activeSkillId ?? ''}
              onChange={(e) => setActiveSkill(e.target.value === '' ? null : (e.target.value as never))}
              disabled={pool.length === 0}
            >
              {pool.length === 0 && <option value="">— keine —</option>}
              {pool.map((id) => (
                <option key={id} value={id}>{id}</option>
              ))}
            </select>
          </div>
        );
      })()}

      <div className="aspect-panel__cards">
        {(Object.keys(ASPECTS) as AspectId[]).map((id) => (
          <AspectCard
            key={id}
            def={ASPECTS[id]}
            level={aspects.levels[id] ?? 0}
            unlocked={aspects.unlocked.includes(id)}
            isPrimary={aspects.primary === id}
            isSecondary={aspects.secondary === id}
            secondaryAvailable={secondaryAvailable}
            matchingPieces={affinityCounts[id] ?? 0}
            cd={cd}
            highestVaultTier={tier}
            onSlot={(slot: AspectSlot) => setAspect(slot, id)}
            onUnslot={(slot: AspectSlot) => slot === 'secondary' && setAspect(slot, null)}
            onInvest={() => investAspect(id)}
          />
        ))}
      </div>
    </section>
  );
}

interface AspectCardProps {
  def: AspectDef;
  level: number;
  unlocked: boolean;
  isPrimary: boolean;
  isSecondary: boolean;
  secondaryAvailable: boolean;
  matchingPieces: number;
  cd: number;
  highestVaultTier: number;
  onSlot: (slot: AspectSlot) => void;
  onUnslot: (slot: AspectSlot) => void;
  onInvest: () => void;
}

function AspectCard({
  def, level, unlocked, isPrimary, isSecondary, secondaryAvailable,
  matchingPieces, cd, highestVaultTier, onSlot, onUnslot, onInvest,
}: AspectCardProps) {
  const slotted = isPrimary || isSecondary;
  const nextLevelCost = aspectLevelUpCost(level);
  const canInvest = level >= 1 && level < 5 && cd >= nextLevelCost;

  return (
    <div
      className={`aspect-card ${slotted ? 'aspect-card--slotted' : ''} ${!unlocked ? 'aspect-card--locked' : ''}`}
      style={{ '--aspect-color': def.themeColor } as React.CSSProperties}
    >
      <div className="aspect-card__header">
        <span className="aspect-card__name">{def.id.toUpperCase()}</span>
        <span className="aspect-card__level">
          {level === 0 ? 'L0' : `L${level}/5`}
        </span>
      </div>

      {!unlocked && (
        <div className="aspect-card__lock-msg">
          Erfordert Vault-Tier {def.unlock.vaultTier} (aktuell: {highestVaultTier})
        </div>
      )}

      <div className="aspect-card__passives">
        {def.passives.map((p) => (
          <div key={p.id} className="aspect-card__passive">
            <span className="aspect-card__passive-name">{p.id}</span>
            <span className="aspect-card__passive-effect">{p.effectKind}</span>
          </div>
        ))}
      </div>

      <div className="aspect-card__active-skill">
        Active: <strong>{def.activeSkillId}</strong>
      </div>

      <div className="aspect-card__affinity">
        {AFFINITY_LABEL(matchingPieces)}
      </div>

      {unlocked && (
        <div className="aspect-card__actions">
          {!slotted && (
            <>
              <button type="button" onClick={() => onSlot('primary')}>Primary</button>
              <button
                type="button"
                onClick={() => onSlot('secondary')}
                disabled={!secondaryAvailable}
                title={!secondaryAvailable ? 'Erfordert Vault-Tier 3' : ''}
              >
                Secondary
              </button>
            </>
          )}
          {isPrimary && (
            <span className="aspect-card__badge aspect-card__badge--primary">★ Primary</span>
          )}
          {isSecondary && (
            <>
              <span className="aspect-card__badge aspect-card__badge--secondary">★ Secondary</span>
              <button type="button" onClick={() => onUnslot('secondary')}>Unslot</button>
            </>
          )}
          {level >= 1 && level < 5 && (
            <button
              type="button"
              className="aspect-card__invest"
              onClick={onInvest}
              disabled={!canInvest}
              title={`Cost: ${nextLevelCost.toLocaleString('de-DE')} ⌬`}
            >
              L{level + 1} ({nextLevelCost.toLocaleString('de-DE')} ⌬)
            </button>
          )}
          {level === 5 && (
            <span className="aspect-card__badge aspect-card__badge--max">✓ Maxed</span>
          )}
        </div>
      )}
    </div>
  );
}
